import { useState, useEffect, useCallback, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { HeaderSection } from './HeaderSection';
import { FamilyMemberSelector } from './FamilyMemberSelector';
import { DashboardContent } from './DashboardContent';
import { useAppleHealth } from '@/hooks/useAppleHealth';
import { HealthDataUpdate } from '@/services/appleHealthService';
import { HealthDataTransmissionService } from '@/services/healthDataTransmissionService';
import { CareCircle } from './CareCircle';
import { DataSources } from './DataSources';
import { DocumentWallet } from './DocumentWallet';
import { MedicalQRCode } from './MedicalQRCode';
import { DisputesWorkflow } from './DisputesWorkflow';
import { HealthScoreGauge } from './HealthScoreGauge';
import { ActivityChart } from './ActivityChart';
import { InsightsSection } from './InsightsSection';
import { AddDataSourceDialog } from './AddDataSourceDialog';
import { AISummary } from './AISummary';
import { requireAuth } from '@/utils/authUtils';
import { getUserInfo } from '@/utils/jwtUtils';
import { fhirDataStore } from '@/utils/fhirDataUtils';
import { HealthScore } from '@/types/healthScore';

interface FamilyMember {
  id: string;
  name: string;
  relationship: string;
  score: number;
  avatar: string;
}

export const HealthDashboard = () => {
  const [selectedMember, setSelectedMember] = useState<string>('me');
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [healthScore, setHealthScore] = useState<HealthScore | null>(null);
  const [aiSummary, setAiSummary] = useState<any | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState('me');
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Apple Health immediate monitoring
  const { hasPermissions, isAvailable, startImmediateMonitoring, stopImmediateMonitoring, service } = useAppleHealth();
  
  // Health data transmission service
  const transmissionServiceRef = useRef<HealthDataTransmissionService | null>(null);
  const isTransmittingRef = useRef<boolean>(false);
  
  // Initialize transmission service
  useEffect(() => {
    if (service) {
      transmissionServiceRef.current = new HealthDataTransmissionService(service);
    }
  }, [service]);
  
  useEffect(() => {
    // Check authentication on component mount
    requireAuth();
    
    // Load family members from FHIR data
    loadFamilyMembersFromStorage();
    
    // Restore last selected patient from localStorage
    const lastSelectedPatient = localStorage.getItem('last_selected_patient');
    if (lastSelectedPatient) {
      setSelectedMember(lastSelectedPatient);
    }
  }, []);

  // Start immediate monitoring when permissions are granted
  useEffect(() => {
    if (hasPermissions && isAvailable) {
      const handleUpdate = async (update: HealthDataUpdate) => {
        console.log('[Health Dashboard] ========================================');
        console.log('[Health Dashboard] 🔔 HEALTH DATA UPDATE RECEIVED');
        console.log('[Health Dashboard] Update type:', update.type);
        console.log('[Health Dashboard] Update value:', update.value);
        console.log('[Health Dashboard] Update unit:', update.unit);
        console.log('[Health Dashboard] Update timestamp:', update.timestamp);
        console.log('[Health Dashboard] ========================================');
        
        // Record the update in the transmission service
        if (transmissionServiceRef.current) {
          transmissionServiceRef.current.recordAppleHealthUpdate(update);
          console.log('[Health Dashboard] ✅ Update recorded in transmission service');
          
          // Automatically transmit data when update is received
          // Prevent concurrent transmissions
          if (!isTransmittingRef.current && transmissionServiceRef.current) {
            isTransmittingRef.current = true;
            try {
              // Get token from localStorage if available (for the ?t= parameter)
              const token = localStorage.getItem('bearer_token') || null;
              
              // Get Google ID from JWT token for persona
              const userInfo = getUserInfo();
              const persona = userInfo?.sub || 'me';
              
              console.log('[Health Dashboard] 🚀 Starting automatic data transmission...');
              console.log('[Health Dashboard] Persona (Google ID):', persona);
              console.log('[Health Dashboard] Token:', token ? 'provided' : 'using timestamp');
              
              // UNCOMMENTED: Actually transmit the data
              await transmissionServiceRef.current.collectAndTransmitHealthData(persona, token);
              
              console.log('[Health Dashboard] ✅ Health data transmitted successfully');
            } catch (error) {
              console.error('[Health Dashboard] ❌ Error transmitting health data:', error);
              console.error('[Health Dashboard] Error details:', error instanceof Error ? error.stack : error);
              // Don't show toast for automatic transmissions to avoid spam
            } finally {
              isTransmittingRef.current = false;
            }
          } else {
            console.log('[Health Dashboard] ⏸️ Transmission already in progress, skipping...');
          }
        } else {
          console.warn('[Health Dashboard] ⚠️ Transmission service not initialized');
        }
      };

      startImmediateMonitoring(handleUpdate).catch(error => {
        console.error('Failed to start immediate monitoring:', error);
      });

      return () => {
        stopImmediateMonitoring().catch(error => {
          console.error('Failed to stop immediate monitoring:', error);
        });
      };
    }
  }, [hasPermissions, isAvailable, startImmediateMonitoring, stopImmediateMonitoring, selectedMember]);

  useEffect(() => {
    // When selected member changes, try to load their score from cache
    if (selectedMember) {
      const cachedScore = localStorage.getItem(`health_score_${selectedMember}`);
      if (cachedScore) {
        try {
          const scoreData = JSON.parse(cachedScore);
          // Simple validation to ensure it's an object with a total
          if (typeof scoreData === 'object' && scoreData !== null && 'total' in scoreData) {
            setHealthScore(scoreData);
          } else {
            // Handle case where cached data is not the expected object
            setHealthScore(null); 
          }
        } catch (e) {
          console.error("Failed to parse score from localStorage", e);
          setHealthScore(null);
        }
      } else {
        setHealthScore(null); // No score in cache
      }
    }
  }, [selectedMember]);

  const loadFamilyMembersFromStorage = async () => {
    const userInfo = getUserInfo();
    const displayName = userInfo?.name || 'User';
    
    // Load score for the current user from localStorage
    const cachedMeScore = localStorage.getItem('health_score_me');
    let healthScore = 0;
    if (cachedMeScore) {
      try {
        const scoreData = JSON.parse(cachedMeScore);
        if (typeof scoreData === 'object' && scoreData !== null && 'total' in scoreData) {
          healthScore = scoreData.total;
        } else if (typeof scoreData === 'number') {
          healthScore = scoreData;
        }
      } catch (e) {
        console.error("Failed to parse score for 'me' from localStorage", e);
      }
    }
    
    const members: FamilyMember[] = [
      {
        id: 'me',
        name: displayName,
        relationship: 'Self',
        score: healthScore,
        avatar: '👤'
      }
    ];

    try {
      // Get all patients from IndexedDB
      const allPatients = await fhirDataStore.listAllPatients();
      
      for (const patientData of allPatients) {
        const patientId = patientData.patientId;
        const bundle = patientData.bundle;
        
        // Find patient resource in the bundle
        const patientEntry = bundle.entry?.find((entry: any) => 
          entry.resource?.resourceType === 'Patient'
        );
        
        if (patientEntry && patientEntry.resource) {
          const patient = patientEntry.resource;
          
          // Extract patient name
          let patientName = 'Unknown Patient';
          if (patient.name && patient.name.length > 0) {
            const name = patient.name[0];
            const given = name.given?.join(' ') || '';
            const family = name.family || '';
            patientName = `${given} ${family}`.trim() || patientId;
          }
          
          // Skip if this is the current user (avoid duplicates)
          if (patientName !== displayName) {
            // Load score from localStorage or default to 0
            const cachedScore = localStorage.getItem(`health_score_${patientId}`);
            let score = 0;
            if (cachedScore) {
              try {
                const scoreData = JSON.parse(cachedScore);
                if (typeof scoreData === 'object' && scoreData !== null && 'total' in scoreData) {
                  score = scoreData.total;
                } else if (typeof scoreData === 'number') {
                  score = scoreData;
                }
              } catch (e) {
                console.error(`Failed to parse score for '${patientId}' from localStorage`, e);
              }
            }
            
            members.push({
              id: patientId,
              name: patientName,
              relationship: 'Family Member',
              score: score,
              avatar: '👤'
            });
          }
        }
      }
    } catch (error) {
      console.error('Error loading family members from IndexedDB:', error);
    }
    
    setFamilyMembers(members);
  };

  const handleMemberSelection = (memberId: string) => {
    setSelectedMember(memberId);
    // Save the selected patient to localStorage
    localStorage.setItem('last_selected_patient', memberId);
  };

  const handleScoreCalculated = useCallback((score: HealthScore) => {
    setHealthScore(score);

    // Also update the score in the family members list for the selected member
    setFamilyMembers(prevMembers =>
      prevMembers.map(member =>
        member.id === selectedMember
          ? { ...member, score: score?.total ?? 0 }
          : member
      )
    );

    // Cache the new score object in localStorage
    if (selectedMember && score) {
      localStorage.setItem(`health_score_${selectedMember}`, JSON.stringify(score));
    }
  }, [selectedMember]);

  const handleSummaryGenerated = useCallback((summary: any) => {
    setAiSummary(summary);
  }, []);

  const handlePatientSelected = (patientId: string) => {
    setSelectedPatientId(patientId);
    // When a new patient is selected, we should reset the score or show a loading state
    setHealthScore(null); 
    setAiSummary(null);
  };

  // Get user info from JWT each time we need it
  const userInfo = getUserInfo();
  
  const currentUser = {
    name: userInfo?.name || 'User',
    score: healthScore?.total ?? familyMembers.find(m => m.id === 'me')?.score ?? 0,
    baselineScore: healthScore?.baseline ?? 180,
    actionableScore: healthScore ? (healthScore.total - healthScore.baseline) : 562,
    lastUpdated: '2 hours ago'
  };

  const displayName = userInfo?.given_name || 'User';

  const generateInsights = (score: any) => {
    if (!score) return [];
    const insights = [];
    if (score.cardio < 50) {
      insights.push({
        type: 'cardio',
        title: 'Cardiovascular Health',
        description: 'Your cardio score is low. Focus on improving blood pressure and cholesterol.',
        action: 'View Details',
      });
    }
    if (score.mental < 50) {
      insights.push({
        type: 'mental',
        title: 'Mental Wellness',
        description: 'Your mental wellness score is low. Consider mindfulness or speaking to a professional.',
        action: 'Learn More',
      });
    }
    if (score.lifestyle < 50) {
      insights.push({
        type: 'lifestyle',
        title: 'Lifestyle Factors',
        description: 'Your lifestyle score is low. Small changes to diet and exercise can help.',
        action: 'Get Tips',
      });
    }
    if (insights.length === 0) {
      insights.push({
        type: 'general',
        title: 'Great Job!',
        description: 'You\'re doing a great job maintaining your health. Keep it up!',
        action: 'See History',
      });
    }
    return insights;
  };

  const handlePatientDataImported = (importedPatientId?: string) => {
    // Refresh the family members list when new patient data is imported
    loadFamilyMembersFromStorage();
    
    // Auto-select the imported patient if provided
    if (importedPatientId) {
      handleMemberSelection(importedPatientId);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const handleTitleClick = () => {
    setActiveTab('dashboard');
  };

  const handleTabChange = (value: string) => {
    console.log('[HealthDashboard] Tab changed to:', value);
    if (value === 'wallet') {
      console.log('Health Wallet Selected');
    }
    setActiveTab(value);
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderSection onTitleClick={handleTitleClick} onLogout={handleLogout} />

      {/* Headless AISummary component for data fetching */}
      <div style={{ display: 'none' }}>
        <AISummary 
          selectedPatientId={selectedMember} 
          onScoreCalculated={handleScoreCalculated}
          onSummaryGenerated={handleSummaryGenerated}
        />
      </div>

      <div className="container mx-auto px-3 md:px-4 py-4 md:py-6">
        <FamilyMemberSelector
          familyMembers={familyMembers}
          selectedMember={selectedMember}
          onMemberSelection={handleMemberSelection}
        />

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4 md:space-y-6">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 gap-1">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="care-circle">Care Circle</TabsTrigger>
            <TabsTrigger value="data-sources">Data Sources</TabsTrigger>
            <TabsTrigger value="wallet">Health Wallet</TabsTrigger>
            <TabsTrigger value="disputes">Disputes</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardContent
              selectedMember={selectedMember}
              familyMembers={familyMembers}
              currentUser={currentUser}
              displayName={displayName}
              aiSummary={aiSummary}
              healthScore={healthScore}
            />
          </TabsContent>

          <TabsContent value="care-circle">
            <CareCircle />
          </TabsContent>

          <TabsContent value="data-sources">
            <DataSources />
          </TabsContent>

          <TabsContent value="wallet">
            <div className="space-y-6">
              <MedicalQRCode selectedPatientId={selectedMember} />
              <DocumentWallet 
                selectedPatientId={selectedMember} 
                onPatientDataImported={handlePatientDataImported}
              />
            </div>
          </TabsContent>

          <TabsContent value="disputes">
            <DisputesWorkflow selectedPatientId={selectedMember} />
          </TabsContent>

          <TabsContent value="insights">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">AI-Powered Health Insights</h2>
              <p className="text-muted-foreground">Advanced analytics and personalized recommendations coming soon...</p>
            </Card>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <HealthScoreGauge
                score={healthScore?.total ?? 0}
                baselineScore={healthScore?.baseline ?? 0}
                actionableScore={(healthScore?.total ?? 0) - (healthScore?.baseline ?? 0)}
              />
              <ActivityChart />
              <InsightsSection insights={generateInsights(healthScore)} />
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
              {/* Additional insights content */}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
